import styled, { createGlobalStyle, css } from 'styled-components';
import { Link } from 'react-router-dom';

export const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; }
  html { scroll-behavior: smooth; }
  html, body, #root { height: 100%; }
  body {
    margin: 0;
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.bg};
    line-height: 1.6;
    letter-spacing: -0.01em;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  section {
    position: relative;
    scroll-margin-top: 80px;
  }
  a { color: ${({ theme }) => theme.colors.brand}; text-decoration: none; }
  img { display: block; max-width: 100%; }
  h1, h2, h3, h4, h5, h6 { margin: 0; letter-spacing: -0.02em; line-height: 1.2; }
  button { font-family: inherit; }
  ::selection {
    background: ${({ theme }) => theme.colors.brand};
    color: #fff;
  }
  :focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.focusRing};
    border-radius: 4px;
  }
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;

export const Container = styled.div`
  max-width: ${({ theme }) => theme.container};
  margin: 0 auto;
`;

export const topBorder = css`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.brand} 0,
      ${({ theme }) => theme.colors.brand} 120px,
      transparent 120px
    );
    pointer-events: none;
  }
`;

/* ---- Shared UI primitives ---- */

export const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  min-height: 44px;
  border: none;
  border-radius: ${({ theme }) => theme.radii.sm}px;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
  text-decoration: none;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transition.base},
    color ${({ theme }) => theme.transition.base},
    border-color ${({ theme }) => theme.transition.base},
    box-shadow ${({ theme }) => theme.transition.base},
    transform ${({ theme }) => theme.transition.base};
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.focusRing};
  }
  &:active {
    transform: translateY(1px);
  }
`;

export const PrimaryButton = styled(Link)`
  ${buttonBase}
  background: ${({ theme }) => theme.colors.brand};
  color: #fff;
  &:hover {
    background: ${({ theme }) => theme.colors.brandDark};
    box-shadow: ${({ theme }) => theme.shadow.sm};
    transform: translateY(-1px);
  }
`;

export const SecondaryButton = styled(Link)`
  ${buttonBase}
  background: ${({ theme }) => theme.colors.cardBg};
  color: ${({ theme }) => theme.colors.brand};
  border: 1px solid ${({ theme }) => theme.colors.border};
  &:hover {
    background: ${({ theme }) => theme.colors.brandTint};
    border-color: ${({ theme }) => theme.colors.brand};
    transform: translateY(-1px);
  }
`;

export const cardBase = css`
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md}px;
  box-shadow: ${({ theme }) => theme.shadow.sm};
  transition: box-shadow ${({ theme }) => theme.transition.slow},
    transform ${({ theme }) => theme.transition.slow};
  &:hover {
    box-shadow: ${({ theme }) => theme.shadow.md};
    transform: translateY(-3px);
  }
`;

export const SectionHeading = styled.h2`
  font-size: clamp(1.6rem, 1.2rem + 1.5vw, 2rem);
  font-weight: 800;
  color: ${({ theme }) => theme.colors.brandDark};
  text-align: center;
  margin-bottom: 0.5rem;
`;

export const SectionSub = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.muted};
  margin: 0 auto 2rem;
  max-width: 640px;
`;
